import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Grid2,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../../views/components/common/SkuCopyRow";
import CustomTextField from "../../../../../../../views/components/common/textFields/TextField";
import AppSearchDropdownMulti from "../../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import AppTablePagenation from "../../../../../../../views/components/common/pages/AppTablePagenation";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import { useDebounce } from "../../../../../../../views/components/common/hooks/Debounce";
import {
  formatDateWithoutTime,
  formatTimeOnly,
} from "../../../../../../../views/components/common/functions/formatDate";
import {
  getModernPageColors,
  modernDrawerHeaderSx,
} from "../../../../../../../styles/modernListingPageSx";
import {
  INVENTORY_ADJUSTMENT_TYPE_OPTIONS,
  InventoryAdjustmentType,
  InventoryAuditLogRow,
} from "../../../../../../../models/SwageazyInventory/SwageazyInventoryAdjustment";
import SwageazyInventoryAdjustmentStore from "../../../../../stores/SwageazyInventoryAdjustmentStore";

interface SwageazyInventoryAuditLogDrawerProps {
  store: SwageazyInventoryAdjustmentStore;
}

type ChipTone = "warning" | "secondary";

/** Same tinted-chip palette as the SKU Audit Ledger (adjust → amber, transfer → purple). */
const ADJUSTMENT_TYPE_META: Record<
  InventoryAdjustmentType,
  { label: string; tone: ChipTone }
> = {
  adjustment: { label: "Adjustment", tone: "warning" },
  sku_transfer: { label: "SKU Transfer", tone: "secondary" },
};

const typeChipSx = (tone: ChipTone) => ({
  fontWeight: 600,
  color: `${tone}.dark`,
  bgcolor: (theme: Theme) => alpha(theme.palette[tone].main, 0.12),
  border: (theme: Theme) => `1px solid ${alpha(theme.palette[tone].main, 0.3)}`,
});

const ellipsisSx = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

const clampSx = (lines: number) =>
  ({
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
  }) as const;

const Muted: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <CustomTypography variant="body2" color="text.disabled">
    {children ?? "—"}
  </CustomTypography>
);

const AuditLogTableRow: React.FC<{ row: InventoryAuditLogRow }> = ({ row }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const isTransfer = row.adjustment_type === "sku_transfer";
  const qty = Number(row.quantity) || 0;
  const typeMeta =
    ADJUSTMENT_TYPE_META[row.adjustment_type] ?? ADJUSTMENT_TYPE_META.adjustment;

  return (
    <TableRow
      hover
      sx={{
        "& td": { py: 1.25, verticalAlign: "top" },
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      {/* Date & Time */}
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <CustomTypography variant="body2" fontWeight={500} color={colors.title}>
          {formatDateWithoutTime(row.created_date, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </CustomTypography>
        <CustomTypography variant="body3" color={colors.subtitle}>
          {formatTimeOnly(row.created_date)}
        </CustomTypography>
      </TableCell>

      {/* Type */}
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Chip label={typeMeta.label} size="small" sx={typeChipSx(typeMeta.tone)} />
        <CustomTypography variant="body3" color={colors.subtitle} sx={{ mt: 0.5 }}>
          {row.source === "csv" ? "CSV" : "Manual"} · #{row.adjustment_id}
        </CustomTypography>
      </TableCell>

      {/* SKU / Product */}
      <TableCell sx={{ maxWidth: 240 }}>
        <SkuCopyRow sku={row.sku} emptyFallback="—" fontWeight={600} color={colors.title} />
        {row.product_name && (
          <CustomTypography
            variant="body3"
            color={colors.subtitle}
            sx={ellipsisSx}
            title={row.product_name}
          >
            {row.product_name}
          </CustomTypography>
        )}
        {isTransfer && (
          <Box sx={{ mt: 0.5 }}>
            <SkuCopyRow
              sku={row.from_sku}
              suffix={` (#${row.from_product_id ?? "—"}) →`}
              emptyFallback="—"
              variant="body3"
            />
            <SkuCopyRow
              sku={row.to_sku}
              suffix={` (#${row.to_product_id ?? "—"})`}
              emptyFallback="—"
              variant="body3"
            />
          </Box>
        )}
      </TableCell>

      {/* Location */}
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <CustomTypography variant="body2" color={colors.title}>
          {row.warehouse_id || "—"}
        </CustomTypography>
        <CustomTypography variant="body3" color={colors.subtitle}>
          Box {row.box_id || "—"}
        </CustomTypography>
      </TableCell>

      {/* Qty change */}
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        {qty === 0 ? (
          <Muted>0</Muted>
        ) : (
          <CustomTypography
            variant="body2"
            fontWeight={700}
            color={qty < 0 ? colors.debit : colors.credit}
          >
            {qty > 0 ? "+" : ""}
            {qty}
          </CustomTypography>
        )}
        <CustomTypography variant="body3" color={colors.subtitle}>
          {row.quantity_before} → {row.quantity_after}
        </CustomTypography>
      </TableCell>

      {/* Admin */}
      <TableCell sx={{ maxWidth: 160 }} title={row.admin_name || undefined}>
        {row.admin_name || row.admin_id ? (
          <CustomTypography variant="body2" color={colors.title} sx={ellipsisSx}>
            {row.admin_name || `#${row.admin_id}`}
          </CustomTypography>
        ) : (
          <Muted />
        )}
        {row.platform && (
          <CustomTypography variant="body3" color={colors.subtitle} sx={ellipsisSx}>
            {row.platform}
          </CustomTypography>
        )}
      </TableCell>

      {/* Reason */}
      <TableCell sx={{ minWidth: 200, maxWidth: 280 }}>
        <CustomTypography
          variant="body2"
          color={colors.title}
          sx={clampSx(2)}
          title={row.reason || undefined}
        >
          {row.reason || "—"}
        </CustomTypography>
        {row.note && (
          <CustomTypography
            variant="body3"
            color={colors.subtitle}
            sx={{ ...clampSx(2), mt: 0.25 }}
            title={row.note}
          >
            Note: {row.note}
          </CustomTypography>
        )}
        {row.sheet_url && (
          <Link
            href={row.sheet_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-block",
              mt: 0.25,
              color: colors.accent,
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            View sheet
          </Link>
        )}
      </TableCell>
    </TableRow>
  );
};

const SwageazyInventoryAuditLogDrawer: React.FC<
  SwageazyInventoryAuditLogDrawerProps
> = ({ store }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [skuSearch, setSkuSearch] = useState("");
  const debouncedSku = useDebounce(skuSearch, 400);

  useEffect(() => {
    if (!store.isAuditOpen) {
      setSkuSearch("");
      return;
    }
    if ((store.auditFilters.sku || "") === debouncedSku.trim()) return;
    store.setAuditFilters({ sku: debouncedSku.trim() || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSku, store.isAuditOpen]);

  const rows = store.auditRows;
  const added = rows
    .filter((row) => row.quantity > 0)
    .reduce((sum, row) => sum + Number(row.quantity), 0);
  const removed = rows
    .filter((row) => row.quantity < 0)
    .reduce((sum, row) => sum + Math.abs(Number(row.quantity)), 0);

  const typeValue = INVENTORY_ADJUSTMENT_TYPE_OPTIONS.filter(
    (option) => option.id === (store.auditFilters.type || ""),
  );

  const headCellSx = {
    fontWeight: 600,
    fontSize: 12,
    color: colors.subtitle,
    textTransform: "uppercase" as const,
    letterSpacing: 0.4,
    whiteSpace: "nowrap" as const,
    backgroundColor: colors.headerBg,
    py: 1.25,
  };

  const summaryTiles = [
    { label: "Entries", value: `${store.auditTotalCount}`, color: colors.title },
    {
      label: isPhone ? "Added (page)" : "Added (this page)",
      value: `+${added}`,
      color: added ? colors.credit : colors.title,
    },
    {
      label: isPhone ? "Removed (page)" : "Removed (this page)",
      value: `−${removed}`,
      color: removed ? colors.debit : colors.title,
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={store.isAuditOpen}
      onClose={store.closeAudit}
      sx={{
        zIndex: 2100,
        "& .MuiDrawer-paper": { zIndex: 2100 },
      }}
    >
      <Box
        sx={{
          width: { xs: "100vw", sm: 760, md: 1080 },
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: theme.palette.mode === "dark" ? "#0F172A" : colors.pageBg,
        }}
      >
        {/* Header */}
        <Box sx={modernDrawerHeaderSx(theme)}>
          <Box sx={{ minWidth: 0 }}>
            <CustomTypography variant="heading4" color={colors.title}>
              Adjustment Audit Logs
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={colors.subtitle}
              sx={{ mt: 0.5, wordBreak: "break-word" }}
            >
              {store.auditTitle || "—"}
              {store.auditFilters.product_id
                ? ` · #${store.auditFilters.product_id}`
                : ""}
            </CustomTypography>
          </Box>
          <IconButton onClick={store.closeAudit} title="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Filters + summary */}
        <Grid2
          container
          spacing={1.5}
          sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2, flexShrink: 0 }}
        >
          <Grid2 size={{ xs: 12, sm: 7 }}>
            <CustomTextField
              placeholder="Filter by SKU"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              width="100%"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 5 }}>
            <AppSearchDropdownMulti
              title="Type"
              isMultiSelect={false}
              local
              list={INVENTORY_ADJUSTMENT_TYPE_OPTIONS}
              value={typeValue}
              handleSelect={(selected) =>
                store.setAuditFilters({
                  type: (selected?.[0]?.id || "") as
                    | InventoryAdjustmentType
                    | "",
                })
              }
            />
          </Grid2>
          {summaryTiles.map((tile) => (
            <Grid2 key={tile.label} size={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.customColors.softCardBorder}`,
                  bgcolor: theme.palette.customColors.softCardBackground,
                }}
              >
                <CustomTypography
                  variant="body3"
                  color={theme.palette.customColors.mutedSlateText}
                  sx={ellipsisSx}
                >
                  {tile.label}
                </CustomTypography>
                <CustomTypography variant="heading5" color={tile.color}>
                  {tile.value}
                </CustomTypography>
              </Paper>
            </Grid2>
          ))}
        </Grid2>

        {/* Table */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <InlineFormError message={store.auditError} sx={{ mt: 0 }} />
          {store.isAuditLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            !store.auditError && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: "center",
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <CustomTypography variant="body2" color={colors.subtitle}>
                  No adjustments or SKU transfers found.
                </CustomTypography>
              </Paper>
            )
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                bgcolor: colors.surface,
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Date &amp; Time</TableCell>
                    <TableCell sx={headCellSx}>Type</TableCell>
                    <TableCell sx={headCellSx}>SKU / Product</TableCell>
                    <TableCell sx={headCellSx}>Location</TableCell>
                    <TableCell sx={headCellSx} align="right">
                      Qty Change
                    </TableCell>
                    <TableCell sx={headCellSx}>Admin</TableCell>
                    <TableCell sx={headCellSx}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <AuditLogTableRow key={row.id} row={row} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {store.auditTotalCount > 0 && (
          <Box
            sx={{
              borderTop: `1px solid ${colors.border}`,
              bgcolor: colors.surface,
              flexShrink: 0,
            }}
          >
            <AppTablePagenation
              fontSize="12px"
              pageNo={store.auditFilters.page_no}
              limit={store.auditFilters.limit}
              totalCount={store.auditTotalCount}
              rowsPerPageOptions={[20, 50, 100]}
              showLabelRowsPerPage={!isPhone}
              onPageChange={(_event, page) =>
                store.setAuditFilters({ page_no: page })
              }
              handleRowsChange={(limit) =>
                store.setAuditFilters({ limit, page_no: 1 })
              }
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default observer(SwageazyInventoryAuditLogDrawer);
